import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AppShell } from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { AdvocateTraining } from "@/components/AdvocateTraining";
import { toast } from "sonner";
import { FileText, Archive, Download, Plus, Search, ListChecks, CheckCircle2 } from "lucide-react";
import { IncidentQuickCapture } from "@/pages/Incidents";
import { savePdfBlob } from "@/pages/GrievanceBuilder";
import { PricingCards } from "@/components/PricingCards";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cases, setCases] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [sort, setSort] = React.useState("updated");
  const [statusFilter, setStatusFilter] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [pendingDraft, setPendingDraft] = React.useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (sort) params.set("sort", sort);
      if (statusFilter) params.set("status", statusFilter);
      const { data } = await api.get(`/cases?${params.toString()}`);
      setCases(data);
    } finally { setLoading(false); }
  };

  React.useEffect(()=>{ load(); }, [sort, statusFilter]);

  const startNew = async () => {
    // If an unfinished Draft exists, ask before creating a brand-new one.
    // Existing drafts never load automatically — user must intentionally continue.
    const draft = cases.find(c => c.status === "Draft" && !c.archived);
    if (draft) { setPendingDraft(draft); return; }
    nav("/new-grievance");
  };

  const continueDraft = () => {
    const d = pendingDraft; setPendingDraft(null);
    if (d) nav(`/new-grievance/${d.id}`);
  };

  const startFreshIgnoreDraft = () => {
    setPendingDraft(null);
    nav("/new-grievance");
  };

  const archive = async (id) => {
    await api.post(`/cases/${id}/archive`);
    toast.success("Case archived");
    load();
  };

  const [pdfBusyId, setPdfBusyId] = React.useState(null);
  const download = async (id, caseNumber) => {
    if (pdfBusyId) return;
    setPdfBusyId(id);
    try {
      const r = await api.get(`/cases/${id}/pdf`, { responseType: "blob" });
      savePdfBlob(r.data, `${caseNumber || "FMG-Grievance"}.pdf`);
      toast.success("Grievance PDF downloaded.");
    } catch (e) {
      if (e.response?.status === 402) {
        toast.info("This PDF requires Premium. Taking you to Billing to see your options.");
        nav("/billing");
      } else {
        toast.error("PDF generation failed. Open the case to check what's still needed.");
      }
    } finally { setPdfBusyId(null); }
  };

  const active = cases.filter(c=>!c.archived && !["Archived","Closed"].includes(c.status));
  const drafts = active.filter(c=>c.status==="Draft");
  const filed = active.filter(c=>["Filed","Meeting Scheduled","Employer Response Received","Pending","Settled","Denied","Withdrawn","Shared With Steward","Ready for Review"].includes(c.status));
  const archived = cases.filter(c=>c.archived || c.status==="Archived");

  const StatusPill = ({s}) => {
    const map = {"Draft":"bg-slate-100 text-slate-700","Ready for Review":"bg-amber-100 text-amber-800","Shared With Steward":"bg-blue-100 text-blue-800","Filed":"bg-red-100 text-red-800","Settled":"bg-green-100 text-green-800","Archived":"bg-slate-200 text-slate-600"};
    return <span className={`text-xs px-2 py-1 rounded font-semibold ${map[s]||"bg-slate-100 text-slate-700"}`}>{s}</span>;
  };

  const CaseCard = ({c}) => (
    <div className="bg-white border border-slate-200 rounded-md p-5 transition-lift hover:shadow-md" data-testid={`case-card-${c.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs text-slate-500 font-mono">{c.case_number}</div>
          <h3 className="font-bold text-slate-900 font-display truncate">{c.title}</h3>
          <div className="text-xs text-slate-500 mt-1">{c.primary_category || "Uncategorized"}</div>
        </div>
        <StatusPill s={c.status}/>
      </div>
      <div className="mt-3 text-xs text-slate-500">Incident: {c.incident_date || "—"} • Updated: {c.updated_at?.slice(0,10)}</div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Link to={`/cases/${c.id}`}><Button size="sm" variant="outline" data-testid={`open-${c.id}`}>Open</Button></Link>
        <Button size="sm" variant="outline" onClick={()=>download(c.id, c.case_number)} disabled={pdfBusyId===c.id} data-testid={`pdf-${c.id}`}><Download className="h-3.5 w-3.5 mr-1"/>{pdfBusyId===c.id ? "Preparing…" : "PDF"}</Button>
        {!c.archived && <Button size="sm" variant="outline" onClick={()=>archive(c.id)} data-testid={`archive-${c.id}`}><Archive className="h-3.5 w-3.5 mr-1"/>Archive</Button>}
      </div>
    </div>
  );

  const Section = ({title, list, empty}) => (
    <section className="mt-8">
      <h2 className="text-xl font-black font-display text-slate-900 mb-4">{title} <span className="text-slate-400 text-sm font-normal">({list.length})</span></h2>
      {list.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center text-slate-500 text-sm">{empty}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{list.map(c=><CaseCard key={c.id} c={c}/>)}</div>
      )}
    </section>
  );

  return (
    <AppShell>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black font-display text-slate-900" data-testid="dashboard-welcome">Welcome, {user?.first_name}.</h1>
          <p className="text-slate-600 text-sm mt-1">Manage your grievances, drafts, and filed cases.</p>
        </div>
        <Button data-testid="new-grievance-btn" onClick={startNew} className="bg-red-600 hover:bg-red-700 text-white"><Plus className="h-4 w-4 mr-1"/>Start New Grievance</Button>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
        <div className="md:col-span-2"><IncidentQuickCapture onCreated={()=>toast.success("Incident saved to your history")}/></div>
        <Link to="/incidents" className="bg-white border border-slate-200 rounded-md p-6 transition-lift hover:shadow-md flex flex-col justify-between" data-testid="incidents-card">
          <div>
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest"><ListChecks className="h-4 w-4"/>Incident history</div>
            <div className="mt-3 font-black font-display text-2xl text-slate-900">Incident Timeline</div>
            <p className="text-sm text-slate-600 mt-1">Review, combine, or convert past incidents.</p>
          </div>
          <div className="mt-4 text-sm text-red-600 font-semibold">Open →</div>
        </Link>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input data-testid="dash-search" placeholder="Search cases..." value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&load()} className="pl-9 pr-3 py-2 w-full border border-slate-300 rounded-md"/>
        </div>
        <select data-testid="dash-sort" value={sort} onChange={e=>setSort(e.target.value)} className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white">
          <option value="updated">Recently updated</option><option value="newest">Newest</option><option value="oldest">Oldest</option><option value="incident">Incident date</option><option value="status">Status</option>
        </select>
        <select data-testid="dash-status" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white">
          <option value="">All statuses</option>
          {["Draft","Ready for Review","Shared With Steward","Filed","Meeting Scheduled","Employer Response Received","Pending","Settled","Denied","Withdrawn","Closed","Archived"].map(s=><option key={s}>{s}</option>)}
        </select>
      </div>

      {loading ? <div className="mt-8 text-slate-500 text-sm">Loading...</div> : (
        <>
          <Section title="Draft Cases" list={drafts} empty="No drafts. Start a new grievance to get going."/>
          <Section title="Active / Filed Cases" list={filed} empty="No active filed cases yet."/>
          <Section title="Archived" list={archived} empty="Nothing archived."/>
        </>
      )}

      <AlertDialog open={!!pendingDraft} onOpenChange={(o)=>{ if(!o) setPendingDraft(null); }}>
        <AlertDialogContent data-testid="unfinished-draft-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>You have an unfinished draft</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDraft ? `${pendingDraft.case_number} — ${pendingDraft.title || "Untitled"}` : ""}
              <br/>Continue editing it, or start a brand-new blank grievance. Your existing draft will stay in your Draft Cases either way.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unfinished-continue-btn" onClick={continueDraft}>Continue Draft</AlertDialogCancel>
            <AlertDialogAction data-testid="unfinished-start-new-btn" onClick={startFreshIgnoreDraft} className="bg-red-600 hover:bg-red-700 text-white">Start New Grievance</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

// Stable, module-scope input field for Profile. Declaring this outside the
// Profile function is critical — inline component definitions inside a parent
// component create a new component type on every render, which unmounts and
// remounts each input on every keystroke and drops focus after one character.
// (React.memo is intentionally NOT used here: the root fix is module-scope
// identity + a plain render function, not memoization.)
function ProfileField({ k, label, type = "text", req, value, onChange, autoComplete, readOnly }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">
        {label}{req && <span className="text-red-600"> *</span>}
      </span>
      <input
        data-testid={`profile-${k}`}
        type={type}
        value={value}
        onChange={onChange}
        required={req}
        autoComplete={autoComplete}
        readOnly={readOnly}
        aria-readonly={readOnly || undefined}
        className={`w-full border border-slate-300 rounded-md px-3 py-2 ${readOnly ? "bg-slate-100 text-slate-700 cursor-not-allowed" : ""}`}
      />
    </label>
  );
}

// The only employer supported by the platform today. Any value that looks like
// UPS is normalized to this canonical string on load and on save so members
// cannot accidentally persist "U.P.S.", "Ups", "ups inc", etc. Additional
// employers can be added here in the future without touching the Profile UI.
const CANONICAL_EMPLOYER = "UPS";
function _normalizeEmployer(raw) {
  const s = String(raw || "").trim();
  if (!s) return CANONICAL_EMPLOYER;
  const compact = s.replace(/[.\s,]+/g, "").toLowerCase();
  if (compact === "ups" || compact.startsWith("upsinc") || compact.startsWith("unitedparcelservice")) {
    return CANONICAL_EMPLOYER;
  }
  // Unknown / unrelated employer values are coerced to UPS today; keeps
  // contract-eligibility behavior consistent for the audience we serve.
  return CANONICAL_EMPLOYER;
}

// Normalized bargaining-unit Job Classification taxonomy for the Profile
// dropdown. Grouped for member-facing usability. The raw selected label is
// what gets persisted as `job_classification` on the profile — no coercion,
// no separate enum value.
const JOB_CLASSIFICATION_GROUPS = [
  { group: "Drivers", options: [
    "Package Car Driver",
    "Feeder Driver",
    "Air Driver",
    "Cover Driver",
    "TCD — Temporary Cover Driver",
    "Yard Shifter / Shifter",
  ]},
  { group: "Inside / Operations", options: [
    "Preloader",
    "Sorter",
    "Loader",
    "Unloader",
    "Part-Time Inside",
    "Part-Time Air",
    "22.3 Full-Time Combination",
    "22.4 Combination Driver",
  ]},
  { group: "Maintenance / Support", options: [
    "Mechanic / Automotive",
    "Utility",
    "Porter / Car Washer",
  ]},
  { group: "Office / Administrative", options: [
    "Admin / Clerical",
  ]},
  { group: "Other", options: [
    "Seasonal",
    "Other — Specify",
  ]},
];
const ALL_JOB_CLASSIFICATIONS = JOB_CLASSIFICATION_GROUPS.flatMap(g => g.options);
const OTHER_SPECIFY = "Other — Specify";

// Dropdown + conditional free-text field for job_classification. Preserves
// legacy free-text values by routing an unrecognized non-empty value into
// the "Other — Specify" branch with the value pre-populated. Never overwrites
// or coerces the existing profile value.
function JobClassificationField({ value, onChange }) {
  const knownMatch = value && ALL_JOB_CLASSIFICATIONS.includes(value);
  const [dropdown, setDropdown] = React.useState(() => {
    if (!value) return "";
    if (knownMatch) return value;
    return OTHER_SPECIFY;
  });
  const [otherText, setOtherText] = React.useState(() => {
    if (value && !knownMatch) return value;
    return "";
  });
  // Keep local state in sync when parent value changes (e.g. profile reloads).
  React.useEffect(() => {
    if (!value) { setDropdown(""); setOtherText(""); return; }
    if (ALL_JOB_CLASSIFICATIONS.includes(value)) {
      setDropdown(value);
      setOtherText("");
    } else {
      setDropdown(OTHER_SPECIFY);
      setOtherText(value);
    }
  }, [value]);

  const handleDropdown = (v) => {
    setDropdown(v);
    if (v === OTHER_SPECIFY) {
      // Preserve any typed value; if empty, hold the sentinel so save doesn't
      // silently drop the classification.
      onChange(otherText || OTHER_SPECIFY);
    } else {
      setOtherText("");
      onChange(v);
    }
  };
  const handleOther = (v) => {
    setOtherText(v);
    onChange(v || OTHER_SPECIFY);
  };

  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">Job classification</span>
      <select
        data-testid="profile-job_classification"
        value={dropdown}
        onChange={(e) => handleDropdown(e.target.value)}
        className="w-full border border-slate-300 rounded-md px-3 py-2 focus:border-red-500 focus:outline-none"
      >
        <option value="">Select classification...</option>
        {JOB_CLASSIFICATION_GROUPS.map(g => (
          <optgroup key={g.group} label={g.group}>
            {g.options.map(o => <option key={o} value={o}>{o}</option>)}
          </optgroup>
        ))}
      </select>
      {dropdown === OTHER_SPECIFY && (
        <input
          data-testid="profile-job_classification-other"
          type="text"
          value={otherText}
          onChange={(e) => handleOther(e.target.value)}
          placeholder="Enter your bargaining-unit classification"
          className="mt-2 w-full border border-slate-300 rounded-md px-3 py-2 focus:border-red-500 focus:outline-none"
        />
      )}
    </label>
  );
}

// Standard browser autocomplete hints for personal-information + address
// fields. Only fields that map to a documented WHATWG token are set; UPS-
// specific fields (local_union, work_location, etc.) are left unset.
const AUTOCOMPLETE_MAP = {
  first_name: "given-name",
  last_name: "family-name",
  phone: "tel",
  street_address: "address-line1",
  city: "address-level2",
  state: "address-level1",
  zip_code: "postal-code",
};

export function Profile() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const { hash } = useLocation();
  React.useEffect(() => {
    if (hash === "#advocate-program" || hash === "#advocate") {
      setTimeout(() => document.getElementById("advocate-program")?.scrollIntoView({ behavior: "smooth" }), 150);
    }
  }, [hash]);
  const [f, setF] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  // Hydration guard: the full user from /auth/me always carries a `profile`
  // object; the minimal login payload does not. Never seed (or allow saving)
  // an editable form from an unhydrated user — a falsely blank save would
  // overwrite stored values.
  const hydrated = !!user?.profile;
  React.useEffect(()=>{
    if (user && !user.profile) refresh();
  }, [user]);
  React.useEffect(()=>{
    if (user?.profile) setF({
      first_name: user.first_name,
      last_name: user.last_name,
      ...(user.profile || {}),
      // Prefill / normalize employer on every load so the value in state
      // matches what the read-only field displays and what save will send.
      employer: _normalizeEmployer(user.profile?.employer),
    });
  }, [user]);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      // Always persist the canonical employer value regardless of local state.
      await api.put("/profile", { ...f, employer: _normalizeEmployer(f.employer) });
      toast.success("Profile saved");
      await refresh();
      nav("/dashboard");
    }
    catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
    finally { setBusy(false); }
  };
  const onFieldChange = React.useCallback((k) => (e) => {
    const v = e.target.value;
    setF(prev => ({ ...prev, [k]: v }));
  }, []);
  // Render helper that returns a <ProfileField> element directly. Using a
  // function that returns JSX (rather than defining a new component) avoids
  // introducing a fresh component identity on each render, which would remount
  // the input and drop focus after each keystroke.
  const F = (k, label, opts = {}) => (
    <ProfileField key={k} k={k} label={label} type={opts.type || "text"} req={opts.req}
      autoComplete={AUTOCOMPLETE_MAP[k]}
      readOnly={opts.readOnly}
      value={opts.readOnly ? (opts.value ?? "") : (f[k] || "")}
      onChange={opts.readOnly ? undefined : onFieldChange(k)} />
  );
  return (
    <AppShell>
      <h1 className="text-3xl font-black font-display text-slate-900">Employee Profile</h1>
      <p className="text-slate-600 mt-1 text-sm">Fields marked * are required to file. Everything else can wait.</p>
      <form onSubmit={save} className="mt-6 bg-white border border-slate-200 rounded-md p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {F("first_name", "First name", { req: true })}
          {F("last_name", "Last name", { req: true })}
          {F("phone", "Phone")}
          {F("employee_id", "Employee ID")}
          {F("street_address", "Street address")}
          {F("city", "City")}
          {F("state", "State")}
          {F("zip_code", "ZIP")}
        </div>
        <div className="pt-6 border-t border-slate-200">
          <h3 className="font-bold font-display text-slate-900">Employment</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {F("employer", "Employer", { req: true, readOnly: true, value: CANONICAL_EMPLOYER })}
            {F("work_location", "Work location")}
            {F("local_union", "Local union number", { req: true })}
            <JobClassificationField
              value={f.job_classification || ""}
              onChange={(v) => setF(prev => ({ ...prev, job_classification: v }))}
            />
            {F("seniority_date", "Seniority date", { type: "date" })}
            {F("hire_date", "Hire date", { type: "date" })}
            {F("preferred_supplement", "Preferred supplement / rider")}
            {F("steward_name", "Steward name")}
            {F("business_agent_name", "Business agent name")}
          </div>
        </div>
        <Button data-testid="profile-save" disabled={busy || !hydrated} type="submit" className="bg-red-600 hover:bg-red-700 text-white">{!hydrated ? "Loading profile..." : busy ? "Saving..." : "Save profile"}</Button>
      </form>
      <div id="advocate-program">
        <AdvocateCard/>
        <AdvocateTraining/>
      </div>
    </AppShell>
  );
}

/** FMG Advocate Program entry point for already-registered members (Build 1).
 *  Reads the FMG-side interest signal only. Authoritative Advocate records
 *  will live in the future FMG Command Center. Failure here never affects
 *  the member's access to FileMyGrievance.
 */
function AdvocateCard() {
  const [status, setStatus] = React.useState(null);   // null = loading, or "not_requested" / "pending_sync" / other
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [cc, setCc] = React.useState(null);           // Command Center orientation/certification status

  React.useEffect(() => {
    let alive = true;
    api.get("/advocate/status")
      .then(r => { if (alive) setStatus(r.data?.status || "not_requested"); })
      .catch(() => { if (alive) setStatus("not_requested"); });
    return () => { alive = false; };
  }, []);

  React.useEffect(() => {
    if (status !== "pending_sync" && status !== "active") return;
    let alive = true;
    api.post("/advocate/training", { action: "get_orientation_status" })
      .then(r => { if (alive) setCc(r.data?.data ?? r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [status]);

  // Mirror AdvocateTraining's tolerant certified derivation so both cards
  // can never contradict each other for the same authoritative CC response.
  const _norm = (v) => String(v || "").toLowerCase();
  const ccTraining = cc?.training_status ? _norm(cc.training_status) : null;
  const certified = !!cc && (_norm(cc.certification_status) === "certified" || cc.certified === true || ccTraining === "completed");
  const orientationRequired = !!cc && !certified && ccTraining === "not_started";
  const orientationInProgress = !!cc && !certified && !!ccTraining && ccTraining !== "not_started" && ccTraining !== "completed";
  const openOrientation = () => {
    window.dispatchEvent(new CustomEvent("fmg-open-orientation"));
  };
  const code = cc?.advocate_code || cc?.referral_code || cc?.code || null;
  const link = cc?.referral_link || cc?.referral_url || cc?.advocate_link ||
    (code ? `${window.location.origin}/?ref=${code}` : null);
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Advocate link copied."); }
    catch { toast.error("Could not copy. Long-press or select the link to copy."); }
  };

  const requestAdvocate = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/advocate/request");
      setStatus(data?.status || "pending_sync");
      toast.success("Advocate request received. We'll be in touch as the program rolls out.");
    } catch (e) {
      setErr("Could not submit right now. Please try again later.");
    } finally {
      setBusy(false);
    }
  };

  if (certified) {
    return (
      <section className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="advocate-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold tracking-widest text-red-700 uppercase">FMG Advocate Program</div>
            <h3 className="mt-1 font-black font-display text-slate-900 text-lg" data-testid="advocate-certified-heading">Certified FMG Advocate</h3>
            {code && (
              <p className="mt-3 text-sm text-slate-700" data-testid="advocate-code">Your Advocate Code: <span className="font-mono font-semibold">{code}</span></p>
            )}
            {link && (
              <p className="mt-1 text-sm text-slate-700 break-all" data-testid="advocate-link">Your Advocate Link: <span className="font-mono">{link}</span></p>
            )}
            {link && (
              <Button size="sm" onClick={copyLink} data-testid="copy-advocate-link-btn" className="mt-3 bg-red-600 hover:bg-red-700 text-white">
                Copy Advocate Link
              </Button>
            )}
          </div>
          <div className="min-w-[160px] flex flex-col items-start sm:items-end gap-2">
            <span data-testid="advocate-badge-active" className="inline-block text-[11px] font-semibold tracking-widest uppercase border rounded-full px-3 py-1 bg-green-100 text-green-800 border-green-200">Status: Active</span>
            <span data-testid="advocate-badge-certified" className="inline-block text-[11px] font-semibold tracking-widest uppercase border rounded-full px-3 py-1 bg-green-50 text-green-700 border-green-200">Certification: Certified</span>
          </div>
        </div>
      </section>
    );
  }

  if (orientationRequired || orientationInProgress) {
    return (
      <section className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="advocate-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold tracking-widest text-red-700 uppercase">FMG Advocate Program</div>
            <h3 className="mt-1 font-black font-display text-slate-900 text-lg" data-testid="advocate-orientation-heading">
              {orientationRequired ? "Orientation Required" : "Orientation In Progress"}
            </h3>
            <p className="mt-2 text-sm text-slate-600" data-testid="advocate-orientation-copy">
              Your FMG Advocate request has been accepted.<br/>
              Complete the short FMG Advocate Orientation to become certified.
            </p>
            <Button size="sm" onClick={openOrientation} data-testid="start-orientation-cta" className="mt-4 bg-red-600 hover:bg-red-700 text-white">
              {orientationRequired ? "Start Advocate Orientation" : "Continue Advocate Orientation"}
            </Button>
          </div>
          <span data-testid="advocate-badge-orientation" className="inline-block text-[11px] font-semibold tracking-widest uppercase border rounded-full px-3 py-1 bg-amber-100 text-amber-800 border-amber-200">
            {orientationRequired ? "Orientation Required" : "Orientation In Progress"}
          </span>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="advocate-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold tracking-widest text-red-700 uppercase">FMG Advocate Program</div>
          <h3 className="mt-1 font-black font-display text-slate-900 text-lg">Become an Advocate</h3>
          <p className="mt-1 text-sm text-slate-600 leading-relaxed">
            Help introduce FileMyGrievance to other UPS Teamsters. The FMG Advocate Program is reviewed and managed separately by our team. Optional at all times.
          </p>
        </div>
        <div className="min-w-[160px] flex flex-col items-start sm:items-end gap-2">
          {status === null ? (
            <span className="text-xs text-slate-400" data-testid="advocate-loading">Loading…</span>
          ) : status === "pending_sync" ? (
            <span data-testid="advocate-badge-pending" className="inline-block text-[11px] font-semibold tracking-widest uppercase border rounded-full px-3 py-1 bg-amber-100 text-amber-800 border-amber-200">Request Received</span>
          ) : status === "not_requested" ? (
            <Button data-testid="advocate-request-btn" onClick={requestAdvocate} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white">
              {busy ? "Submitting…" : "Request to Become an Advocate"}
            </Button>
          ) : (
            // Any other status (e.g. future Command-Center-owned values) is displayed read-only.
            <span data-testid="advocate-badge-other" className="inline-block text-[11px] font-semibold tracking-widest uppercase border rounded-full px-3 py-1 bg-slate-100 text-slate-700 border-slate-200">{String(status).replace(/_/g," ")}</span>
          )}
          {err && <p className="text-xs text-red-600" data-testid="advocate-err">{err}</p>}
        </div>
      </div>
    </section>
  );
}

export function Notifications() {
  const [list, setList] = React.useState([]);
  const load = async () => setList((await api.get("/notifications")).data);
  React.useEffect(()=>{ load(); }, []);
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <AppShell>
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-black font-display">Notifications</h1>
        <Button variant="outline" onClick={markAll} data-testid="notif-mark-all">Mark all read</Button>
      </div>
      <div className="mt-6 bg-white border border-slate-200 rounded-md divide-y divide-slate-200">
        {list.length === 0 ? <div className="p-8 text-center text-slate-500">No notifications.</div> :
          list.map(n=>(
            <div key={n.id} className={`p-4 flex justify-between ${n.read?"":"bg-red-50"}`} data-testid={`notif-${n.id}`}>
              <div>
                <div className="font-semibold text-slate-900">{n.title}</div>
                <div className="text-sm text-slate-600">{n.body}</div>
              </div>
              <div className="text-xs text-slate-500">{n.created_at?.slice(0,10)}</div>
            </div>
          ))}
      </div>
    </AppShell>
  );
}

export function ContractLibrary() {
  const [q, setQ] = React.useState("");
  const [articles, setArticles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const load = async () => {
    setLoading(true); setError("");
    try {
      setArticles((await api.get(`/articles${q?`?q=${encodeURIComponent(q)}`:''}`)).data);
    } catch {
      setError("We couldn't load the Contract Library right now. Please try again in a moment.");
    } finally { setLoading(false); }
  };
  React.useEffect(()=>{ load(); }, []);
  return (
    <AppShell>
      <h1 className="text-3xl font-black font-display">Contract Library</h1>
      <p className="text-slate-600 mt-1 text-sm">Plain-language summaries. Always review the original contract text with your steward.</p>
      <div className="mt-6 flex gap-2 max-w-lg">
        <input data-testid="lib-search" placeholder="Search article #, title, keyword..." value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&load()} className="flex-1 border border-slate-300 rounded-md px-3 py-2"/>
        <Button onClick={load} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="lib-search-btn">Search</Button>
      </div>
      {loading ? (
        <div className="mt-6 bg-white border border-slate-200 rounded-md p-8 text-center text-slate-500 text-sm" data-testid="lib-loading">Searching the contract library…</div>
      ) : error ? (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-8 text-center text-red-700 text-sm" data-testid="lib-error">{error}</div>
      ) : articles.length === 0 ? (
        <div className="mt-6 bg-white border border-dashed border-slate-300 rounded-md p-8 text-center text-slate-500 text-sm" data-testid="lib-no-results">No matching contract articles found. Try a different keyword or article number.</div>
      ) : (
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {articles.map(a=>(
          <div key={a.id} className="bg-white border border-slate-200 rounded-md p-5" data-testid={`article-${a.article_number.replace(/[.\s]/g,'')}`}>
            <div className="text-xs text-red-600 font-semibold tracking-wide">{a.article_number}</div>
            <h3 className="font-bold font-display text-slate-900 mt-1">{a.article_title}</h3>
            <p className="text-sm text-slate-600 mt-2">{a.article_summary}</p>
            <div className="text-xs text-slate-500 mt-3">{a.contract_name}</div>
          </div>
        ))}
      </div>
      )}
    </AppShell>
  );
}

export function Billing() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const [portalBusy, setPortalBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [annual, setAnnual] = React.useState(true);
  const [caseCount, setCaseCount] = React.useState(null);
  React.useEffect(() => {
    (async () => {
      if (user?.plan === "free") {
        try {
          const { data } = await api.get("/cases");
          const FINALIZED = ["Filed","Meeting Scheduled","Employer Response Received","Pending","Settled","Denied","Closed"];
          setCaseCount((data || []).filter(c => FINALIZED.includes(c.status)).length);
        } catch { setCaseCount(0); }
      }
    })();
  }, [user?.plan]);
  const upgrade = async (lookupKeyOverride) => {
    setBusy(true); setError("");
    try {
      const lookup_key = lookupKeyOverride || (annual ? "premium_annual" : "premium_monthly");
      const { data } = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { setError(e.response?.data?.detail || "Checkout failed. Stripe may not be configured."); }
    finally { setBusy(false); }
  };
  const switchPlan = async () => {
    // Switching monthly<->annual is done safely through the Stripe billing portal
    // so we don't double-bill via a second Checkout session.
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not open billing portal.");
      setBusy(false);
    }
  };
  const openPortal = async () => {
    setPortalBusy(true); setError("");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not open billing portal.");
      setPortalBusy(false);
    }
  };
  const [showCancelConfirm, setShowCancelConfirm] = React.useState(false);
  const cancel = async () => {
    setShowCancelConfirm(false);
    try {
      const { data } = await api.post("/billing/cancel");
      toast.success(data.message || "Subscription canceled");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };
  const isPremium = user?.plan === 'premium';
  const isCelebration = !isPremium && caseCount !== null && caseCount >= 1;

  // Celebration variant — Free user who has created their first grievance
  if (isCelebration) {
    return (
      <AppShell>
        <div className="max-w-4xl mx-auto" data-testid="billing-celebration">
          <div className="relative overflow-hidden rounded-lg border border-red-100 bg-gradient-to-br from-red-50 via-white to-white p-8 lg:p-12">
            <div className="text-4xl" aria-hidden>🎉</div>
            <h1 className="mt-4 text-3xl sm:text-4xl font-black font-display text-slate-900 tracking-tight" data-testid="billing-celebration-title">
              Great Work Protecting Your Workplace Rights
            </h1>
            <p className="mt-4 text-lg text-slate-700 max-w-2xl">
              You've successfully created your first grievance and started building your Workplace Timeline.
            </p>
            <p className="mt-3 text-slate-700 max-w-2xl">
              Your Workplace Timeline, notes, evidence, and first grievance are securely saved and will always remain available to you.
            </p>
            <div className="mt-5 inline-flex items-start gap-3 bg-white/70 border border-red-100 rounded-md px-4 py-3 max-w-2xl">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5"/>
              <p className="text-sm text-slate-700"><span className="font-semibold text-slate-900">Nothing you've created will ever be lost.</span> Your Workplace Timeline, documentation, and first grievance remain yours regardless of your subscription status.</p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Premium unlocks card */}
            <div className="lg:col-span-3 bg-slate-900 text-white rounded-lg p-8 relative overflow-hidden" data-testid="billing-celebration-premium">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs font-semibold tracking-widest text-red-400 uppercase">Premium</div>
                  <p className="mt-2 text-sm text-slate-300">If you decide to create another grievance, Premium gives you access to:</p>
                </div>
                <div className="inline-flex bg-slate-800 border border-slate-700 rounded-full p-1" data-testid="billing-toggle">
                  <button onClick={()=>setAnnual(false)} className={`px-3 py-1 rounded-full text-xs font-semibold ${!annual?"bg-slate-700 text-white":"text-slate-400"}`} data-testid="billing-toggle-monthly">Monthly</button>
                  <button onClick={()=>setAnnual(true)} className={`px-3 py-1 rounded-full text-xs font-semibold ${annual?"bg-slate-700 text-white":"text-slate-400"}`} data-testid="billing-toggle-annual">Annual (-17%)</button>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-slate-200">
                <li>• Unlimited grievance creation</li>
                <li>• Advanced AI guidance</li>
                <li>• Enhanced contract analysis</li>
                <li>• Smarter case organization</li>
                <li>• Future premium features as they are released</li>
              </ul>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="text-4xl font-black font-display" data-testid="billing-price">{annual ? "$49.95" : "$4.95"}</span>
                <span className="text-sm text-slate-400">{annual ? "/year" : "/month"}</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">{annual ? "≈ $4.16/month billed annually — Save 16%" : "Billed monthly"}</p>
              <Button disabled={busy} onClick={upgrade} className="mt-5 bg-red-600 hover:bg-red-700 text-white w-full h-12" data-testid="billing-upgrade">
                {busy ? "Redirecting..." : "Upgrade to Premium"}
              </Button>
              <Link to="/incidents" className="block">
                <Button variant="ghost" className="mt-2 w-full text-slate-300 hover:text-white hover:bg-slate-800" data-testid="billing-continue-free">
                  Continue Building My Timeline
                </Button>
              </Link>
              {error && <p className="mt-3 text-sm text-red-300" data-testid="billing-error">{error}</p>}
            </div>

            {/* Reassurance panel */}
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6">
              <div className="text-xs font-semibold tracking-widest text-slate-500 uppercase">Yours to keep</div>
              <h3 className="mt-2 font-display font-bold text-slate-900 text-lg">Your Workplace Timeline always remains available</h3>
              <p className="mt-3 text-sm text-slate-600">Even if you continue using the free plan, every incident, note, photo, and case you've built stays with you. Documentation is always free at FileMyGrievance.</p>
              <div className="mt-5 space-y-2 text-sm text-slate-700">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0"/>Unlimited incident recording</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0"/>All evidence &amp; notes preserved</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0"/>Access your first grievance anytime</div>
              </div>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  // Default variant — Premium user OR Free user with zero cases yet
  const currentLookupKey = user?.subscription_plan || null;
  return (
    <AppShell>
      <h1 className="text-3xl font-black font-display">Billing</h1>
      <p className="text-slate-600 mt-1 text-sm">Start free. Upgrade when it matters.</p>

      {/* Current plan summary (unchanged) */}
      <div className="mt-6 bg-white border border-slate-200 rounded-md p-6 max-w-xl">
        <div className="text-sm font-semibold text-slate-500">Current plan</div>
        <div className="mt-2 text-2xl font-black font-display" data-testid="billing-current-plan">{user?.plan?.toUpperCase() || "FREE"}</div>
        <p className="text-xs text-slate-500 mt-2">
          {isPremium
            ? (user?.cancel_at_period_end
                ? "Premium — canceling at period end."
                : "Thanks for supporting workers.")
            : "You're on the Free plan — 1 free grievance included. Incident Recorder is unlimited."}
        </p>
        {isPremium && (
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={openPortal} disabled={portalBusy} data-testid="billing-manage">
              {portalBusy ? "Opening..." : "Manage subscription"}
            </Button>
            <Button variant="ghost" className="text-red-600 hover:text-red-700" onClick={()=>setShowCancelConfirm(true)} data-testid="billing-cancel">
              Cancel subscription
            </Button>
            <ConfirmDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm} title="Cancel your subscription?" description="You'll keep Premium access until the end of the current billing period." confirmLabel="Cancel Subscription" onConfirm={cancel} />
          </div>
        )}
      </div>

      {/* Pricing cards — same as public Plans page, reflecting current plan */}
      <div className="mt-10">
        <PricingCards
          mode="member"
          currentPlan={isPremium ? "premium" : "free"}
          currentLookupKey={currentLookupKey}
          onSelectMonthly={() => upgrade("premium_monthly")}
          onSelectAnnual={() => upgrade("premium_annual")}
          onSwitchPlan={switchPlan}
          busy={busy}
          error={error}
        />
      </div>
    </AppShell>
  );
}

export function BillingSuccess() {
  const [status, setStatus] = React.useState("verifying"); // verifying | paid | timeout | error
  const [msg, setMsg] = React.useState("Verifying your payment...");
  const { refresh } = useAuth();
  const nav = useNavigate();
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  const poll = async (n=0) => {
    if (n > 12) { setStatus("timeout"); setMsg("Still working on it… Your payment may still succeed. Refresh or contact support if this persists."); return; }
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid" || data.status === "complete") {
        setStatus("paid");
        setMsg("Payment successful! Premium is now active on your account.");
        await refresh();
        return;
      }
    } catch { /* fallthrough */ }
    setTimeout(()=>poll(n+1), 2000);
  };
  React.useEffect(()=>{
    if (!sessionId) { setStatus("error"); setMsg("Missing session id."); return; }
    poll();
  }, []);

  // Auto-redirect to dashboard 3s after paid
  React.useEffect(()=>{
    if (status === "paid") {
      const t = setTimeout(()=> nav("/dashboard"), 3500);
      return () => clearTimeout(t);
    }
  }, [status]);

  return (
    <AppShell>
      <div className="max-w-xl mx-auto mt-8 bg-white border border-slate-200 rounded-lg p-10 text-center" data-testid="billing-success">
        <div className="text-5xl mb-4" aria-hidden>
          {status === "paid" ? "✓" : status === "verifying" ? "…" : "!"}
        </div>
        <h1 className="text-3xl font-black font-display">
          {status === "paid" ? "Thank you!" : status === "verifying" ? "Almost there" : "Payment status"}
        </h1>
        <p className="mt-3 text-slate-600" data-testid="billing-success-msg">{msg}</p>
        {status === "paid" && (
          <p className="mt-1 text-xs text-slate-500">Redirecting you to your dashboard…</p>
        )}
        <div className="mt-6 flex justify-center gap-3">
          <Link to="/dashboard"><Button className="bg-red-600 hover:bg-red-700 text-white" data-testid="billing-success-dashboard">Go to dashboard</Button></Link>
          {status !== "paid" && <Link to="/billing"><Button variant="outline" data-testid="billing-success-retry">Back to billing</Button></Link>}
        </div>
      </div>
    </AppShell>
  );
}

export function BillingCancel() {
  return (
    <AppShell>
      <h1 className="text-3xl font-black font-display">Payment canceled</h1>
      <p className="mt-2 text-slate-600" data-testid="billing-cancel-msg">Your payment was canceled. You can retry any time.</p>
      <Link to="/billing"><Button className="mt-4 bg-red-600 hover:bg-red-700 text-white">Back to billing</Button></Link>
    </AppShell>
  );
}
